import React, { createContext, useMemo, useContext, useCallback, useState, useRef } from "react";
import axios from "axios";
import Fuse from "fuse.js";
import UpshowLogger from "../../Logger";
import { eventsSearchKeys, fuseSearch } from "@upshow/live-events";
import { fetchAndCacheState } from "../../Upshow";
import { getHeaders } from "../utils/shared/headers";
import { getActiveLiveContent, categorizeEvents } from "../utils/shared/events";
import { EVENT_STATUS, CONTENT_TYPE } from "../constants/event";
import { BASE_URL, CACHE_TTL } from "../constants/general";
import { trackPendoEvent } from "../utils/shared/pendo";

const EventsContext = createContext();

export const EventsProvider = ({ children }) => {
    const [liveEventsData, setLiveEventData] = useState([]);
    const [upcomingEventsData, setUpcomingEventData] = useState([]);
    const [scheduledEventsData, setScheduledEventsData] = useState([]);
    const [completeEventsData, setCompleteEventsData] = useState([]);
    const [activeEventData, setActiveEventData] = useState(null);
    const [channelsData, setChannelsData] = useState([]);

    const [loading, setLoading] = useState(false);
    const lastFetchTimeRef = useRef(0);

    // Filter status
    const [showFiltered, setShowFiltered] = useState(false);
    const [selectedSport, setSelectedSport] = useState(null);
    const [filteredType, setFilteredType] = useState("sport");

    // Fetch the current live content
    const fetchActiveLiveContent = useCallback(async () => {
        await fetchAndCacheState(); // Re-fetch upshow state
        const activeLiveContent = getActiveLiveContent() || {};
        setActiveEventData(activeLiveContent);
    }, []);

    // Handle aprox. search on events array
    const searchEvents = useCallback((events, searchTerm) => {
        return fuseSearch(Fuse, events, eventsSearchKeys, searchTerm);
    }, []);

    // Fetch channels
    const fetchChannels = useCallback(async () => {
        try {
            const { data } = await axios.get(`${BASE_URL}/v6/channels`, {
                headers: { ...getHeaders(), "Cache-Control": "no-cache" },
                params: { _: Date.now() },
            });
            const channels = (data.channels || []).map(ch => ({
                ...ch,
                contentType: CONTENT_TYPE.LIVE_CHANNEL,
                status: EVENT_STATUS.LIVE,
            }));
            setChannelsData(channels);
        } catch (err) {
            UpshowLogger.error(["channels"], err);
        }
    }, []);

    // Fetch events (only on open) if no events yet or cache expired
    const fetchEvents = useCallback(
        async (force = false) => {
            const now = Date.now();
            if (!force && lastFetchTimeRef.current !== 0 && now - lastFetchTimeRef.current < CACHE_TTL) {
                return;
            }
            try {
                setLoading(true);
                const { data } = await axios.get(`${BASE_URL}/v6/events`, {
                    headers: { ...getHeaders(), "Cache-Control": "no-cache" },
                    params: { _: Date.now() },
                });

                const { live, upcoming, scheduled } = categorizeEvents(data.events);

                setLiveEventData(live);
                setUpcomingEventData(upcoming);
                setScheduledEventsData(scheduled);
                setCompleteEventsData(data.events);

                // Fetch and change active live content
                fetchActiveLiveContent();
                fetchChannels();

                lastFetchTimeRef.current = Date.now();
            } catch (err) {
                UpshowLogger.error(["events"], err);
            } finally {
                setLoading(false);
            }
        },
        [fetchActiveLiveContent, fetchChannels]
    );

    // Filter logic
    const handleFilterSelect = useCallback(
        (item, options = { type: "sport" }) => {
            if (!item || !completeEventsData || completeEventsData.length === 0) return;
            if (options.type) setFilteredType(options.type);
            setSelectedSport(item);
            setShowFiltered(true);
            // Track pendo event
            const sectionType = options.type;
            const sectionName = item.alias || item.name;
            trackPendoEvent(`Select category filter - ${sectionType} - ${sectionName}`)
        },
        [completeEventsData]
    );

    const filteredEvents = useCallback(() => {
        let results = [];
        if (!showFiltered || !selectedSport) return results;

        if (filteredType === "league" || filteredType === "partner") {
            if (selectedSport.searchType) {
                if (selectedSport.searchType === "league") {
                    results = completeEventsData.filter((event) => 
                        event.leagues?.some((league) => league.alias.toLowerCase().includes(selectedSport.searchTerm.toLowerCase()))
                    );
                } else if (selectedSport.searchType === "conference") {
                    results = completeEventsData.filter((event) => 
                        event.conferences?.some((conference) => conference.alias.toLowerCase().includes(selectedSport.searchTerm.toLowerCase()))
                    );
                } else if (selectedSport.searchType === "subscription_type") {
                    results = completeEventsData.filter((event) => 
                        event.subscriptionType?.backwards_compatible_name.toLowerCase().includes(selectedSport.searchTerm.toLowerCase())
                    );
                }
            } else {
                results = searchEvents(completeEventsData, selectedSport.searchTerm);
            }
        } else if (filteredType === "sport") {
            // Filter by sport ID (matches id in event.sports array)
            if (selectedSport?.id) {
                results = completeEventsData.filter((ev) => 
                    Array.isArray(ev.sports) && ev.sports.some((s) => String(s.id) === String(selectedSport.id))
                );
            }
        }
        
        return results;
    }, [showFiltered, selectedSport, filteredType, completeEventsData, searchEvents]);

    const resetFilter = useCallback(() => {
        setShowFiltered(false);
        setSelectedSport(null);
    }, []);

    const value = useMemo(
        () => ({
            loading,
            liveEvents: liveEventsData,
            upcomingEvents: upcomingEventsData.slice(0, 20),
            scheduledEvents: scheduledEventsData,
            completeLiveEvents: completeEventsData,
            activeLiveContent: activeEventData,
            setActiveEventData,
            channels: channelsData,
            fetchEvents,
            fetchActiveLiveContent,
            searchEvents,
            showFiltered,
            selectedSport,
            filteredType,
            handleFilterSelect,
            filteredEvents,
            resetFilter,
        }),
        [loading, liveEventsData, upcomingEventsData, scheduledEventsData, completeEventsData, activeEventData, setActiveEventData, channelsData, fetchEvents, fetchActiveLiveContent, searchEvents, showFiltered, selectedSport, filteredType, handleFilterSelect, filteredEvents, resetFilter]
    );

    return <EventsContext.Provider value={value}>{children}</EventsContext.Provider>;
};

export const useEvents = () => {
    const context = useContext(EventsContext);
    if (context === undefined) {
        return null;
    }
    return context;
};
